/**
 * iter108 — Forgot Password screen.
 *
 * Multi-step machine:
 *   1. CHOOSE_CHANNEL — Mobile vs Email
 *   2. ENTER_IDENTIFIER — phone or email input
 *   3. ENTER_OTP — 6-digit OTP entry + resend
 *   4. NEW_PASSWORD — new + confirm + strength rules
 *   5. DONE — success state → "Back to Login"
 *
 * All API calls go through `/api/auth/forgot-password/*` which returns
 * generic responses (no account enumeration). The UI surfaces real errors
 * only for weak/mismatched passwords and OTP-invalid (latter is generic too).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Phone, KeyRound, Lock, Loader2, AlertCircle,
  CheckCircle2, Eye, EyeOff, Shield, RotateCcw
} from 'lucide-react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const STRONG_RULES = [
  { id: 'len', label: '8+ characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'Uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'Lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'num', label: 'Number', test: (p) => /\d/.test(p) },
  { id: 'sym', label: 'Symbol', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const ForgotPasswordScreen = () => {
  const navigate = useNavigate();
  // iter138 — Email OTP removed. Only mobile OTP is exposed to users.
  // The initial CHOOSE_CHANNEL step is skipped; users land directly on
  // ENTER_IDENTIFIER with `channel` pre-selected as 'mobile'. Backend
  // still accepts both channels for backward compat.
  const [step, setStep] = useState('ENTER_IDENTIFIER'); // ENTER_IDENTIFIER | ENTER_OTP | NEW_PASSWORD | DONE
  const [channel] = useState('mobile');                 // permanently mobile
  const [identifier, setIdentifier] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);

  useEffect(() => {
    if (otpTimer <= 0) return undefined;
    const t = setInterval(() => setOtpTimer((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpTimer]);

  const passwordChecks = useMemo(() => STRONG_RULES.map((r) => ({ ...r, ok: r.test(newPassword) })), [newPassword]);
  const passwordStrong = passwordChecks.every((r) => r.ok);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  // iter138 — chooseChannel retained for a no-op safety (in case any legacy
  // ref remains), but the UI never calls it now.
  const chooseChannel = () => {
    setError('');
    setStep('ENTER_IDENTIFIER');
  };

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    setError('');
    setInfo('');
    if (!identifier.trim()) {
      setError('Enter your registered mobile number');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/forgot-password/request`, {
        identifier: identifier.trim(),
        channel,
      });
      const result = data?.RESULT || {};
      // Always advance to OTP step — even if the identifier doesn't exist,
      // we don't reveal that to the user.
      setSessionId(result.session_id);
      setOtpTimer(result.ttl_seconds || 300);
      setInfo(data?.MESSAGE || 'If an account exists, an OTP has been sent.');
      setStep('ENTER_OTP');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.MESSAGE || err?.message;
      setError(typeof detail === 'string' ? detail : 'Could not send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!otp.trim() || otp.trim().length < 4) {
      setError('Enter the OTP you received');
      return;
    }
    if (!sessionId) {
      setError('Session expired. Please restart the flow.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/forgot-password/verify-otp`, {
        session_id: sessionId,
        otp: otp.trim(),
      });
      const result = data?.RESULT || {};
      setResetToken(result.reset_token);
      setStep('NEW_PASSWORD');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(typeof detail === 'string' ? detail : 'OTP expired or invalid.');
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!passwordStrong) {
      setError('Password does not meet all the security rules below.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/api/auth/forgot-password/reset`, {
        reset_token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setStep('DONE');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(typeof detail === 'string' ? detail : 'Could not reset password.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setError('');
    // iter138 — CHOOSE_CHANNEL removed. ENTER_IDENTIFIER is now the entry
    // step; going back from it returns the user to /login directly.
    if (step === 'ENTER_IDENTIFIER') navigate('/login');
    else if (step === 'ENTER_OTP') setStep('ENTER_IDENTIFIER');
    else if (step === 'NEW_PASSWORD') setStep('ENTER_OTP');
    else navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EEF1FC] via-white to-[#EEF1FC] flex items-center justify-center p-4" data-testid="forgot-password-screen">
      <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <button
            type="button"
            onClick={goBack}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
            data-testid="forgot-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Forgot Password</h1>
        </div>

        {/* iter138 — Step 1 CHOOSE_CHANNEL block removed. Email OTP is no
            longer surfaced to users; the flow starts directly on
            ENTER_IDENTIFIER with `channel` fixed to 'mobile'. */}

        {/* Step 2 — Enter identifier */}
        {step === 'ENTER_IDENTIFIER' && (
          <form onSubmit={requestOtp} className="space-y-4" data-testid="forgot-enter-identifier">
            <p className="text-sm text-gray-600">
              Enter your registered mobile number to receive an OTP.
            </p>
            <div className="relative">
              <Phone className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="tel"
                value={identifier}
                onChange={(e) => { setIdentifier(e.target.value); setError(''); }}
                placeholder="10-digit mobile"
                className="w-full pl-10 pr-3 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-[#1B2A7B] focus:ring-2 focus:ring-emerald/20 transition-all"
                data-testid="forgot-identifier-input"
                autoFocus
                inputMode="numeric"
                maxLength={120}
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="forgot-error">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !identifier.trim()}
              className="w-full py-3 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="forgot-send-otp-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Send OTP
            </button>
          </form>
        )}

        {/* Step 3 — OTP */}
        {step === 'ENTER_OTP' && (
          <form onSubmit={verifyOtp} className="space-y-4" data-testid="forgot-enter-otp">
            {info && (
              <div className="flex items-start gap-2 p-3 bg-[#EEF1FC] border border-[#1B2A7B]/30 rounded-xl">
                <Shield className="w-4 h-4 text-[#1B2A7B] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-[#1B2A7B]">{info}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Enter 6-digit OTP</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                placeholder="••••••"
                className="w-full text-center text-2xl tracking-[0.5em] py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-[#1B2A7B] focus:ring-2 focus:ring-emerald/20"
                data-testid="forgot-otp-input"
                autoFocus
              />
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <span>{otpTimer > 0 ? `Expires in ${Math.floor(otpTimer / 60)}:${String(otpTimer % 60).padStart(2, '0')}` : 'OTP expired'}</span>
                <button
                  type="button"
                  onClick={requestOtp}
                  disabled={loading || otpTimer > 240}  // allow resend only after ~1 minute
                  className="inline-flex items-center gap-1 text-[#1B2A7B] hover:text-[#1B2A7B]-dark disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="forgot-resend-otp"
                >
                  <RotateCcw className="w-3 h-3" /> Resend
                </button>
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="forgot-error">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={loading || otp.length < 4}
              className="w-full py-3 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="forgot-verify-otp-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              Verify OTP
            </button>
          </form>
        )}

        {/* Step 4 — New password */}
        {step === 'NEW_PASSWORD' && (
          <form onSubmit={submitNewPassword} className="space-y-4" data-testid="forgot-new-password">
            <p className="text-sm text-gray-600">Choose a strong new password for your account.</p>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">New Password</label>
              <div className="relative">
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                  placeholder="New password"
                  className="w-full pl-10 pr-11 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-[#1B2A7B] focus:ring-2 focus:ring-emerald/20"
                  data-testid="forgot-new-password-input"
                  autoFocus
                />
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  placeholder="Confirm new password"
                  className="w-full pl-10 pr-3 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-[#1B2A7B] focus:ring-2 focus:ring-emerald/20"
                  data-testid="forgot-confirm-password-input"
                />
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-red-600 mt-1" data-testid="forgot-mismatch-warning">Passwords do not match.</p>
              )}
            </div>
            <ul className="grid grid-cols-2 gap-1 text-xs" data-testid="forgot-password-rules">
              {passwordChecks.map((r) => (
                <li key={r.id} className={`flex items-center gap-1 ${r.ok ? 'text-[#1B2A7B]' : 'text-gray-400'}`}>
                  <CheckCircle2 className="w-3 h-3" />
                  <span>{r.label}</span>
                </li>
              ))}
            </ul>
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="forgot-error">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !passwordStrong || !passwordsMatch}
              className="w-full py-3 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="forgot-submit-new-password"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Update Password
            </button>
          </form>
        )}

        {/* Step 5 — Done */}
        {step === 'DONE' && (
          <div className="text-center space-y-4 py-4" data-testid="forgot-done">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#EEF1FC] flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-[#1B2A7B]" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Password updated</h2>
            <p className="text-sm text-gray-600">Sign in with your new password.</p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full py-3 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark"
              data-testid="forgot-back-to-login"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;
